<p-toast></p-toast>
<app-page-shell>
  <app-page-hero eyebrow="HR Country Packs" title="Country Packs" subtitle="Configure tenant country packs, employee compliance profiles, statutory payroll previews, WPS/bank exports, and end-of-service assumptions.">
    <div actions>
      <p-select [options]="countryOptions" optionLabel="label" optionValue="value" [(ngModel)]="selectedCountry" (ngModelChange)="changeCountry($event)" class="country-select"></p-select>
      <button pButton type="button" icon="fa-solid fa-rotate" label="Refresh" severity="secondary" outlined (click)="loadAll()"></button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  <app-hr-list-tabs
    [tabs]="[
      { key: 'marketplace', label: 'Marketplace', icon: 'fa-solid fa-store', badge: packs.length },
      { key: 'settings', label: 'Active Settings', icon: 'fa-solid fa-toggle-on', badge: settings.length },
      { key: 'requirements', label: 'Requirements', icon: 'fa-solid fa-file-shield', badge: requirements.length },
      { key: 'profiles', label: 'Employee Profiles', icon: 'fa-solid fa-id-card', badge: profiles.length },
      { key: 'rules', label: 'Statutory Rules', icon: 'fa-solid fa-scale-balanced', badge: rules.length },
      { key: 'outputs', label: 'Payroll Outputs', icon: 'fa-solid fa-file-invoice-dollar', badge: outputs.length },
      { key: 'exports', label: 'Export Batches', icon: 'fa-solid fa-file-export', badge: exportBatches.length },
      { key: 'eos', label: 'EOSB Previews', icon: 'fa-solid fa-person-walking-arrow-right', badge: eosPreviews.length }
    ]"
    [(activeTab)]="activeListTab"
  ></app-hr-list-tabs>

  <section class="country-pack-grid">
    <app-page-data-panel [hidden]="activeListTab !== 'marketplace'" title="Pack Marketplace" subtitle="Versioned country packs seeded for SA, AE, IN, and PK.">
      <p-table [value]="packs" responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th>Country</th>
            <th>Pack</th>
            <th>Currency</th>
            <th>Status</th>
            <th>Versions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-pack>
          <tr>
            <td>{{ pack.countryCode }}</td>
            <td>
              <strong>{{ pack.packName }}</strong>
              <small>{{ pack.packCode }}</small>
            </td>
            <td>{{ pack.defaultCurrencyCode }}</td>
            <td><p-tag [severity]="severity(pack.packStatus)" [value]="pack.packStatus"></p-tag></td>
            <td>{{ pack.versions?.length || 0 }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="5">No country packs found.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>

    <app-hr-action-dialog [visible]="actionDialogVisible('activation')" (visibleChange)="setActionDialogVisible('activation', $event)" title="Activate Country Pack" eyebrow="Tenant Compliance" subtitle="Activate a reviewed pack version for this owning company." icon="fa-solid fa-toggle-on" [contextTitle]="selectedCountry + ' country pack'" contextSubtitle="Activation controls payroll, compliance, and statutory export surfaces.">
      <app-page-form-section icon="fa-solid fa-toggle-on" title="Tenant Activation" subtitle="Activate a reviewed pack version for this owning company.">
      <div class="form-grid">
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-code-branch"></i></p-inputGroupAddon>
          <p-iftalabel><p-select inputId="packVersion" [options]="versionOptions" optionLabel="label" optionValue="value" [(ngModel)]="settingForm.countryPackVersionId" class="w-full"></p-select><label for="packVersion">Pack version</label></p-iftalabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-map"></i></p-inputGroupAddon>
          <p-iftalabel><input pInputText id="regionCode" [(ngModel)]="settingForm.defaultRegionCode"><label for="regionCode">Default region / subdivision</label></p-iftalabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-note-sticky"></i></p-inputGroupAddon>
          <p-iftalabel><textarea pTextarea id="activationNotes" [(ngModel)]="settingForm.notes"></textarea><label for="activationNotes">Review notes</label></p-iftalabel>
        </p-inputGroup>
      </div>
      <button pButton type="button" icon="fa-solid fa-save" label="Save Activation" [disabled]="!canManagePacks || !settingForm.countryPackVersionId" (click)="activateSelectedVersion()"></button>
      </app-page-form-section>
    </app-hr-action-dialog>
  </section>

  <section class="country-pack-grid">
    <app-page-data-panel [hidden]="activeListTab !== 'settings'" title="Active Tenant Pack Settings" subtitle="Tenant-scoped country pack versions and enabled surfaces.">
      <div actions><button pButton type="button" icon="fa-solid fa-toggle-on" label="Activate Pack" [disabled]="!canManagePacks" (click)="openActionDialog('activation')"></button></div>
      <p-table [value]="settings" responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th>Country</th>
            <th>Pack</th>
            <th>Version</th>
            <th>Payroll</th>
            <th>Compliance</th>
            <th>Exports</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-setting>
          <tr>
            <td>{{ setting.countryCode }}</td>
            <td>{{ setting.packName }}</td>
            <td>{{ setting.versionCode }}</td>
            <td><p-tag [severity]="setting.payrollEnabled ? 'success' : 'secondary'" [value]="setting.payrollEnabled ? 'On' : 'Off'"></p-tag></td>
            <td><p-tag [severity]="setting.complianceEnabled ? 'success' : 'secondary'" [value]="setting.complianceEnabled ? 'On' : 'Off'"></p-tag></td>
            <td><p-tag [severity]="setting.exportsEnabled ? 'success' : 'secondary'" [value]="setting.exportsEnabled ? 'On' : 'Off'"></p-tag></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="6">No active country pack settings found.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>

    <app-page-data-panel [hidden]="activeListTab !== 'requirements'" title="Document Requirements" subtitle="Country-pack compliance documents seeded from configurable pack definitions.">
      <p-table [value]="requirements" responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th>Code</th>
            <th>Document</th>
            <th>Required</th>
            <th>Blocks Payroll</th>
            <th>Review</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-requirement>
          <tr>
            <td>{{ requirement.documentCode }}</td>
            <td>{{ requirement.documentName }}</td>
            <td>{{ requirement.required ? 'Yes' : 'No' }}</td>
            <td>{{ requirement.blocksPayroll ? 'Yes' : 'No' }}</td>
            <td><p-tag [severity]="requirement.externalReviewRequired ? 'warn' : 'success'" [value]="requirement.externalReviewRequired ? 'Required' : 'Done'"></p-tag></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="5">No document requirements found.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  </section>

  <section class="country-pack-grid">
    <app-hr-action-dialog [visible]="actionDialogVisible('profile')" (visibleChange)="setActionDialogVisible('profile', $event)" title="Employee Compliance Profile" eyebrow="Employee Compliance" subtitle="Maintain country-specific identifiers without country-specific employee tables." icon="fa-solid fa-id-card" [contextTitle]="profileForm.employeeId ? 'Employee ' + profileForm.employeeId : 'Select an employee'" [contextSubtitle]="selectedCountry + ' compliance profile'">
      <app-page-form-section icon="fa-solid fa-id-card" title="Employee Compliance Profile" subtitle="Store country-specific identifiers generically without country-specific employee tables.">
      <div class="form-grid">
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="profileEmployee" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="profileForm.employeeId" class="w-full"></p-select><label for="profileEmployee">Employee</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-map"></i></p-inputGroupAddon><p-iftalabel><input pInputText id="profileRegion" [(ngModel)]="profileForm.regionCode"><label for="profileRegion">Region / subdivision</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-passport"></i></p-inputGroupAddon><p-iftalabel><input pInputText id="statutoryIdentifier" [(ngModel)]="profileForm.statutoryIdentifier"><label for="statutoryIdentifier">Statutory identifier</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-shield-halved"></i></p-inputGroupAddon><p-iftalabel><input pInputText id="socialIdentifier" [(ngModel)]="profileForm.socialInsuranceIdentifier"><label for="socialIdentifier">Social insurance identifier</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-file-invoice"></i></p-inputGroupAddon><p-iftalabel><input pInputText id="taxIdentifier" [(ngModel)]="profileForm.taxIdentifier"><label for="taxIdentifier">Tax identifier</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-building-columns"></i></p-inputGroupAddon><p-iftalabel><input pInputText id="bankIdentifier" [(ngModel)]="profileForm.bankPaymentIdentifier"><label for="bankIdentifier">Bank payment identifier</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-code"></i></p-inputGroupAddon><p-iftalabel><textarea pTextarea id="profileJson" [(ngModel)]="profileForm.complianceProfileJson"></textarea><label for="profileJson">Additional JSON</label></p-iftalabel></p-inputGroup>
      </div>
      <button pButton type="button" icon="fa-solid fa-save" label="Save Profile" [disabled]="!canManageProfiles || !profileForm.employeeId" (click)="saveProfile()"></button>
      </app-page-form-section>
    </app-hr-action-dialog>

    <app-page-data-panel [hidden]="activeListTab !== 'profiles'" title="Employee Profiles" subtitle="Latest compliance profiles for the selected country.">
      <div actions><button pButton type="button" icon="fa-solid fa-plus" label="New Employee Profile" [disabled]="!canManageProfiles" (click)="openActionDialog('profile')"></button></div>
      <p-table [value]="profiles" responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th>Employee</th>
            <th>Country</th>
            <th>Statutory</th>
            <th>Social Insurance</th>
            <th>Tax</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-profile>
          <tr>
            <td><strong>{{ profile.employeeName }}</strong><small>{{ profile.employeeNumber }}</small></td>
            <td>{{ profile.countryCode }}</td>
            <td>{{ profile.statutoryIdentifier || '-' }}</td>
            <td>{{ profile.socialInsuranceIdentifier || '-' }}</td>
            <td>{{ profile.taxIdentifier || '-' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="5">No employee compliance profiles found.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  </section>

  <section class="country-pack-grid">
    <app-page-data-panel [hidden]="activeListTab !== 'rules'" title="Statutory Rules" subtitle="Configurable seeded rule definitions; legal values must be externally reviewed before production use.">
      <p-table [value]="rules" responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th>Rule</th>
            <th>Type</th>
            <th>Element</th>
            <th>Basis</th>
            <th>Rate</th>
            <th>Review</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-rule>
          <tr>
            <td><strong>{{ rule.ruleName }}</strong><small>{{ rule.ruleCode }}</small></td>
            <td>{{ rule.ruleType }}</td>
            <td>{{ rule.elementType }}</td>
            <td>{{ rule.sourceBasis }}</td>
            <td>{{ rule.ratePercent || rule.fixedAmount || 0 }}</td>
            <td><p-tag [severity]="severity(rule.externalReviewStatus)" [value]="rule.externalReviewStatus"></p-tag></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="6">No statutory rules found.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>

    <app-hr-action-dialog [visible]="actionDialogVisible('statutory')" (visibleChange)="setActionDialogVisible('statutory', $event)" title="Payroll Statutory Preview" eyebrow="Country Payroll" subtitle="Generate versioned statutory outputs for a draft payroll run." icon="fa-solid fa-scale-balanced" [contextTitle]="selectedCountry + ' statutory rules'" contextSubtitle="The preview stores rule versions and assumptions for review.">
      <app-page-form-section icon="fa-solid fa-scale-balanced" title="Payroll Statutory Preview" subtitle="Generate statutory outputs from active tenant pack rules for a draft payroll run.">
      <div class="form-grid">
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-file-invoice-dollar"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="previewRun" [options]="payrollRunOptions" optionLabel="label" optionValue="value" [(ngModel)]="previewForm.payrollRunId" class="w-full"></p-select><label for="previewRun">Payroll run</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-globe"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="previewCountry" [options]="countryOptions" optionLabel="label" optionValue="value" [(ngModel)]="previewForm.countryCode" class="w-full"></p-select><label for="previewCountry">Country</label></p-iftalabel></p-inputGroup>
      </div>
      <button pButton type="button" icon="fa-solid fa-calculator" label="Preview Statutory Outputs" [disabled]="!canManagePacks || !previewForm.payrollRunId" (click)="previewStatutoryOutputs()"></button>
      </app-page-form-section>
    </app-hr-action-dialog>
  </section>

  <app-page-data-panel [hidden]="activeListTab !== 'outputs'" title="Payroll Statutory Outputs" subtitle="Traceable preview records with country pack code, version, rule code, and assumptions.">
    <div actions><button pButton type="button" icon="fa-solid fa-scale-balanced" label="Generate Preview" [disabled]="!canManagePacks" (click)="openActionDialog('statutory')"></button></div>
    <p-table [value]="outputs" responsiveLayout="scroll">
      <ng-template pTemplate="header">
        <tr>
          <th>Employee</th>
          <th>Pack</th>
          <th>Rule</th>
          <th>Version</th>
          <th>Source</th>
          <th>Output</th>
          <th>Status</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-output>
        <tr>
          <td><strong>{{ output.employeeName }}</strong><small>{{ output.employeeNumber }}</small></td>
          <td>{{ output.countryPackCode || output.countryCode }}</td>
          <td>{{ output.ruleCode }}</td>
          <td>{{ output.ruleVersion }}</td>
          <td>{{ output.sourceAmount | number:'1.2-2' }}</td>
          <td>{{ output.outputAmount | number:'1.2-2' }} {{ output.currencyCode }}</td>
          <td><p-tag [severity]="severity(output.outputStatus)" [value]="output.outputStatus"></p-tag></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="7">No statutory outputs found.</td></tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>

  <section class="country-pack-grid">
    <app-hr-action-dialog [visible]="actionDialogVisible('export')" (visibleChange)="setActionDialogVisible('export', $event)" title="Stage Statutory Export" eyebrow="Country Payroll" subtitle="Create a traceable WPS or bank export batch." icon="fa-solid fa-file-export" [contextTitle]="selectedCountry + ' export'" contextSubtitle="Staging does not mutate payroll payment state.">
      <app-page-form-section icon="fa-solid fa-file-export" title="WPS / Bank Export Staging" subtitle="Create traceable statutory export batches without mutating payroll payment state.">
      <div class="form-grid">
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-table"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="exportFormat" [options]="exportFormatOptions" optionLabel="label" optionValue="value" [(ngModel)]="exportForm.exportFormatId" class="w-full"></p-select><label for="exportFormat">Export format</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-file-invoice-dollar"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="exportRun" [options]="payrollRunOptions" optionLabel="label" optionValue="value" [(ngModel)]="exportForm.payrollRunId" [showClear]="true" class="w-full"></p-select><label for="exportRun">Payroll run</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-note-sticky"></i></p-inputGroupAddon><p-iftalabel><textarea pTextarea id="exportNotes" [(ngModel)]="exportForm.notes"></textarea><label for="exportNotes">Notes</label></p-iftalabel></p-inputGroup>
      </div>
      <button pButton type="button" icon="fa-solid fa-file-export" label="Stage Export" [disabled]="!canManageExports || !exportForm.exportFormatId" (click)="generateExport()"></button>
      </app-page-form-section>
    </app-hr-action-dialog>

    <app-page-data-panel [hidden]="activeListTab !== 'exports'" title="Export Batches" subtitle="Generated export batches and staging totals.">
      <div actions><button pButton type="button" icon="fa-solid fa-file-export" label="Stage Export" [disabled]="!canManageExports" (click)="openActionDialog('export')"></button></div>
      <p-table [value]="exportBatches" responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th>Batch</th>
            <th>Type</th>
            <th>Lines</th>
            <th>Total</th>
            <th>Status</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-batch>
          <tr>
            <td>{{ batch.exportBatchCode }}</td>
            <td>{{ batch.exportType }}</td>
            <td>{{ batch.lineCount }}</td>
            <td>{{ batch.totalAmount | number:'1.2-2' }} {{ batch.currencyCode }}</td>
            <td><p-tag [severity]="severity(batch.exportStatus)" [value]="batch.exportStatus"></p-tag></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="5">No statutory export batches found.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  </section>

  <section class="country-pack-grid">
    <app-hr-action-dialog [visible]="actionDialogVisible('eos')" (visibleChange)="setActionDialogVisible('eos', $event)" title="End-of-Service Preview" eyebrow="Country Compliance" subtitle="Generate a reviewable benefit preview with rule version and assumptions." icon="fa-solid fa-person-walking-arrow-right" [contextTitle]="eosForm.employeeId ? 'Employee ' + eosForm.employeeId : 'Select an employee'" [contextSubtitle]="selectedCountry + ' end-of-service rules'">
      <app-page-form-section icon="fa-solid fa-person-walking-arrow-right" title="End-of-Service Preview" subtitle="Generate reviewable EOSB previews with rule version and input assumptions.">
      <div class="form-grid">
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="eosEmployee" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="eosForm.employeeId" class="w-full"></p-select><label for="eosEmployee">Employee</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-play"></i></p-inputGroupAddon><p-iftalabel><input pInputText id="eosStart" type="date" [(ngModel)]="eosForm.startDate"><label for="eosStart">Start date</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-stop"></i></p-inputGroupAddon><p-iftalabel><input pInputText id="eosEnd" type="date" [(ngModel)]="eosForm.endDate"><label for="eosEnd">End date</label></p-iftalabel></p-inputGroup>
        <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-coins"></i></p-inputGroupAddon><p-iftalabel><p-inputNumber inputId="eosBasis" [(ngModel)]="eosForm.basisAmount" mode="decimal" [minFractionDigits]="2" styleClass="w-full"></p-inputNumber><label for="eosBasis">Basis amount</label></p-iftalabel></p-inputGroup>
      </div>
      <button pButton type="button" icon="fa-solid fa-eye" label="Preview EOSB" [disabled]="!canManagePacks || !eosForm.employeeId" (click)="previewEos()"></button>
      </app-page-form-section>
    </app-hr-action-dialog>

    <app-page-data-panel [hidden]="activeListTab !== 'eos'" title="EOSB Previews" subtitle="Recent generated end-of-service previews.">
      <div actions><button pButton type="button" icon="fa-solid fa-person-walking-arrow-right" label="Generate EOS Preview" [disabled]="!canManagePacks" (click)="openActionDialog('eos')"></button></div>
      <p-table [value]="eosPreviews" responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th>Employee</th>
            <th>Country</th>
            <th>Version</th>
            <th>Years</th>
            <th>Preview</th>
            <th>Review</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-preview>
          <tr>
            <td><strong>{{ preview.employeeName }}</strong><small>{{ preview.employeeNumber }}</small></td>
            <td>{{ preview.countryCode }}</td>
            <td>{{ preview.ruleVersion }}</td>
            <td>{{ preview.serviceYears | number:'1.2-2' }}</td>
            <td>{{ preview.previewAmount | number:'1.2-2' }} {{ preview.currencyCode }}</td>
            <td><p-tag [severity]="severity(preview.externalReviewStatus)" [value]="preview.externalReviewStatus"></p-tag></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="6">No EOSB previews found.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  </section>
</app-page-shell>

